import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast, Toaster } from "sonner";
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const { lang } = useT();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "fr" ? "Une erreur est survenue" : "Something went wrong"));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="forgot-password-page">
      <Toaster theme="light" />
      <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-[#4A5568] hover:text-[#E5276A]">
        <ArrowLeft className="w-3.5 h-3.5" /> {lang === "fr" ? "Retour à la connexion" : "Back to login"}
      </Link>
      <span className="chip-pink mt-6 inline-block">{lang === "fr" ? "Mot de passe oublié" : "Forgot password"}</span>
      <h1 className="font-display text-4xl text-[#1F3A5F] tracking-tight mt-4 font-medium mb-3">
        {lang === "fr" ? "Réinitialiser mon mot de passe" : "Reset your password"}
      </h1>
      <p className="text-sm text-[#4A5568] mb-8">
        {lang === "fr"
          ? "Saisissez votre email. Si un compte existe, vous recevrez un lien pour définir un nouveau mot de passe (valable 30 minutes)."
          : "Enter your email. If an account exists, you'll receive a link to set a new password (valid 30 minutes)."}
      </p>

      {!sent ? (
        <form onSubmit={submit} className="space-y-5 p-6 md:p-8 rounded-2xl border border-[#1F3A5F]/10 bg-white shadow-sm">
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">Email</span>
            <input
              data-testid="forgot-email-input"
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] focus:ring-2 focus:ring-[#E5276A]/10 outline-none rounded-lg px-4 py-3 text-sm text-[#1F3A5F]"
            />
          </label>
          <button
            data-testid="forgot-submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#E5276A] hover:bg-[#C01661] disabled:opacity-50 text-white text-base font-medium transition-colors"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {lang === "fr" ? "Envoyer le lien" : "Send link"}
          </button>
        </form>
      ) : (
        <div className="p-7 rounded-2xl border border-green-200 bg-green-50 text-center" data-testid="forgot-sent">
          <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
          <h2 className="font-display text-2xl text-[#1F3A5F] mt-4">{lang === "fr" ? "Email envoyé" : "Email sent"}</h2>
          <p className="text-sm text-[#4A5568] mt-2">
            {lang === "fr"
              ? "Si un compte est associé à cette adresse, vous recevrez un lien sous quelques minutes. Vérifiez vos spams."
              : "If an account exists, you'll get a link in a few minutes. Check your spam folder."}
          </p>
        </div>
      )}
    </div>
  );
}
